import { Link } from 'react-router-dom';
import type { ProjectTile } from '@dcc/shared';
import { CapacityLayer, Confidence, FreshnessTag, Meter, StateChip, Unknown } from './Primitives';
import { toneFor } from '../lib/tone';
import { duration, relativeTime } from '../lib/format';

/**
 * A project tile answers, in one glance: where are we, what is next, when, how
 * sure, who is on it, and what is stopping it. It never hides an UNKNOWN input
 * behind a healthy aggregate.
 */
export function ProjectTileCard({ tile }: { tile: ProjectTile }) {
  const tone =
    tile.captain?.state === 'SPLIT_BRAIN_DETECTED' ? 'critical' : toneFor('project', tile.status);
  const progressTone =
    tile.status === 'BLOCKED' ? 'critical' : tile.status === 'COMPLETED' ? 'success' : 'active';

  return (
    <article className="panel tile" data-tone={tone} aria-labelledby={`tile-${tile.id}`}>
      <div className="tile__head">
        <div className="inline" style={{ gap: 10, alignItems: 'flex-start' }}>
          <span
            className="tile__accent"
            style={{ ['--accent' as string]: tile.accentColor ?? undefined }}
          />
          <div>
            <h3 className="tile__name" id={`tile-${tile.id}`}>
              <Link to={`/projects/${tile.id}/control`}>{tile.name}</Link>
            </h3>
            <div className="tile__version">
              {tile.versionKey ?? 'no version'} {tile.versionTitle ? `· ${tile.versionTitle}` : ''}
            </div>
          </div>
        </div>
        <StateChip kind="project" state={tile.status} />
      </div>

      <p className="tile__reason">{tile.statusReason}</p>

      <div className="stack stack--tight">
        <div className="tile__row">
          <span className="dim">Journey</span>
          <span className="mono">
            {tile.journeyCompletionPercent}% · {tile.completedNodes}/{tile.totalNodes} nodes
          </span>
        </div>
        <Meter
          value={tile.journeyCompletionPercent}
          tone={progressTone}
          ariaLabel={`${tile.name} journey completion`}
        />
      </div>

      <div className="stack stack--tight small">
        <div className="tile__row">
          <span className="dim">Now</span>
          <span className="truncate">{tile.currentPositionLabel ?? 'nothing in motion'}</span>
        </div>
        <div className="tile__row">
          <span className="dim">Next testable</span>
          <span className="truncate">{tile.nextStationLabel ?? 'none scheduled'}</span>
        </div>
        <div className="tile__row">
          <span className="dim">Finish forecast</span>
          <span>
            {tile.forecast.etaAt ? (
              <>
                {relativeTime(tile.forecast.etaAt)}{' '}
                <span className="dim">({duration(tile.forecast.totalHours)})</span>
              </>
            ) : (
              <Unknown why={tile.forecast.reasons.join(' ')} />
            )}
          </span>
        </div>
      </div>

      <div className="inline inline--tight">
        <Confidence
          confidence={tile.forecast.confidence}
          percentValue={tile.forecast.confidencePercent}
          reasons={tile.forecast.reasons}
        />
        {tile.captain ? (
          <StateChip
            kind="lease"
            state={tile.captain.state}
            label={`captain ${tile.captain.ownerName ?? tile.captain.ownerSessionId ?? 'unknown'} · epoch ${tile.captain.epoch}`}
          />
        ) : (
          <StateChip kind="lease" state="NONE" label="no captain" />
        )}
        <StateChip kind="connection" state={tile.connectionState} />
      </div>

      {/* When a source is not healthy, say what that means for this project
          rather than leaving a state chip to speak for itself. */}
      {tile.connectionState !== 'CONNECTED' && <p className="tiny dim">{tile.connectionDetail}</p>}

      <div className="tile__facts">
        <CapacityLayer layer={tile.sessionRange} title="Session range" compact />
        <CapacityLayer layer={tile.weeklyFuel} title="Weekly fuel" compact />
      </div>

      <div
        className="spread small"
        style={{ paddingTop: 12, borderTop: '1px solid var(--border)' }}
      >
        <span className="dim">
          {tile.activeMembers.length > 0
            ? tile.activeMembers.map((m) => m.name).join(', ')
            : 'no colleague assigned'}{' '}
          · {tile.activeAgents} agent{tile.activeAgents === 1 ? '' : 's'}
        </span>
        <span className="inline inline--tight">
          {tile.openBlockers > 0 && (
            <span className="chip" data-tone="critical">
              {tile.openBlockers} blocker{tile.openBlockers === 1 ? '' : 's'}
            </span>
          )}
          {tile.openDecisions > 0 && (
            <span className="chip" data-tone="risk">
              {tile.openDecisions} decision{tile.openDecisions === 1 ? '' : 's'}
            </span>
          )}
        </span>
      </div>

      <div className="spread">
        <FreshnessTag freshness={tile.freshness} prefix="source" />
        <span className="inline inline--tight">
          <Link className="btn btn--sm" to={`/projects/${tile.id}/control`}>
            Control
          </Link>
          <Link className="btn btn--sm btn--quiet" to={`/projects/${tile.id}/journey`}>
            Journey
          </Link>
        </span>
      </div>
    </article>
  );
}
